import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { ArrowLeft, CreditCard, Trash2, Plus, CheckCircle2, AlertCircle, Info } from 'lucide-react'
import { getParentByIdOptions, createSetupSessionMutation, getPaymentMethodsOptions, deletePaymentMethodMutation } from '@/lib/client/@tanstack/react-query.gen'
import { toast } from 'sonner'
import { z } from 'zod'

const searchSchema = z.object({
  success: z.boolean().optional(),
  error: z.boolean().optional(),
})

export const Route = createFileRoute('/link/$parentId/payment-methods')({
  component: PaymentMethodsPage,
  validateSearch: searchSchema,
})

function PaymentMethodsPage() {
  const { parentId } = Route.useParams()
  const { success, error } = Route.useSearch()
  const queryClient = useQueryClient()

  const { data: parent, isLoading: loadingParent } = useQuery({
    ...getParentByIdOptions({
      path: { id: parentId },
    }),
  })

  const { data: paymentMethodsData, isLoading: loadingMethods, refetch } = useQuery(getPaymentMethodsOptions({
    path: { id: parentId }
  }))

  const { mutate: createSetupSession, isPending: isCreatingSession } = useMutation({
    ...createSetupSessionMutation(),
    onSuccess: (sessionUrl) => {
      if (!sessionUrl || typeof sessionUrl !== 'string') {
        toast.error('URL de session invalide')
        return
      }
      window.location.href = sessionUrl
    },
    onError: () => {
      toast.error('Erreur lors de la création de la session Stripe')
    },
  })

  const deletePaymentMethodMut = useMutation({
    ...deletePaymentMethodMutation(),
    onSuccess: () => {
      toast.success('Moyen de paiement supprimé')
      refetch()
    },
    onError: () => {
      toast.error('Erreur lors de la suppression')
    },
  })

  const handleDeletePaymentMethod = (paymentMethodId: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer ce moyen de paiement ?')) {
      deletePaymentMethodMut.mutate({
        path: {
          id: parentId,
          paymentMethodId: paymentMethodId
        }
      })
    }
  }

  const handleAddPaymentMethod = () => {
    createSetupSession({ path: { parentId } })
  }

  if (loadingParent || loadingMethods) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  const hasCustomerId = parent?.stripeCustomerId != null
  const hasPaymentMethods = paymentMethodsData?.hasPaymentMethods || false
  const paymentMethods = paymentMethodsData?.paymentMethods || []

  const getPaymentMethodIcon = (type: string) => {
    switch (type) {
      case 'card':
        return <CreditCard className="h-5 w-5 text-gray-400" />
      case 'sepa_debit':
        return <CreditCard className="h-5 w-5 text-gray-400" />
      default:
        return <CreditCard className="h-5 w-5 text-gray-400" />
    }
  }

  const getPaymentMethodLabel = (method: any) => {
    if (method.type === 'card') {
      return `${method.brand?.toUpperCase()} •••• ${method.last4}`
    } else if (method.type === 'sepa_debit') {
      return `SEPA •••• ${method.last4}`
    }
    return method.type?.toUpperCase()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/link/$parentId" params={{ parentId }}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour au tableau de bord
            </Link>
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Mes moyens de paiement</h1>
          <p className="text-gray-600 mt-2">Gérez vos moyens de paiement pour les prélèvements automatiques</p>
        </div>

        {success && (
          <Alert className="mb-6 border-green-500 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-900">Moyen de paiement ajouté !</AlertTitle>
            <AlertDescription className="text-green-800">
              Votre moyen de paiement a été enregistré avec succès.
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert className="mb-6 border-red-500 bg-red-50">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertTitle className="text-red-900">Erreur</AlertTitle>
            <AlertDescription className="text-red-800">
              Une erreur est survenue lors de l'ajout du moyen de paiement.
            </AlertDescription>
          </Alert>
        )}

        {!hasCustomerId && (
          <Alert className="mb-6 border-blue-500 bg-blue-50">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-900">Information</AlertTitle>
            <AlertDescription className="text-blue-800">
              Aucun moyen de paiement n'est encore enregistré. Vous pouvez en ajouter un pour faciliter le règlement de vos factures.
              <br />
              <strong>Rassurez-vous :</strong> Aucun prélèvement ne sera effectué sans votre accord préalable.
            </AlertDescription>
          </Alert>
        )}

        {hasCustomerId && !hasPaymentMethods && (
          <Alert className="mb-6 border-blue-500 bg-blue-50">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-900">Aucun moyen de paiement</AlertTitle>
            <AlertDescription className="text-blue-800">
              Vous n'avez pas encore ajouté de moyen de paiement. Ajoutez-en un pour simplifier vos paiements.
              <br />
              <strong>Rassurez-vous :</strong> Aucun prélèvement ne sera effectué automatiquement sans votre consentement.
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Moyens de paiement enregistrés</CardTitle>
                <CardDescription>
                  {hasPaymentMethods ? `${paymentMethods.length} moyen(s) de paiement` : 'Aucun moyen de paiement'}
                </CardDescription>
              </div>
              <Button
                onClick={handleAddPaymentMethod}
                disabled={isCreatingSession}
                size="sm"
              >
                <Plus className="mr-2 h-4 w-4" />
                {isCreatingSession ? 'Chargement...' : 'Ajouter'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {hasPaymentMethods ? (
              <div className="space-y-4">
                {paymentMethods.map((method: any) => (
                  <div
                    key={method.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        {getPaymentMethodIcon(method.type)}
                        <div>
                          <p className="font-medium text-gray-900">
                            {getPaymentMethodLabel(method)}
                          </p>
                          {method.type === 'card' && method.expMonth && method.expYear && (
                            <p className="text-sm text-gray-500">
                              Expire le {method.expMonth}/{method.expYear}
                            </p>
                          )}
                          <Badge variant="outline" className="mt-1">
                            {method.type === 'card' ? 'Carte bancaire' :
                             method.type === 'sepa_debit' ? 'Prélèvement SEPA' :
                             method.type}
                          </Badge>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeletePaymentMethod(method.id)}
                        disabled={deletePaymentMethodMut.isPending}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CreditCard className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Aucun moyen de paiement enregistré</p>
                <Button onClick={handleAddPaymentMethod} disabled={isCreatingSession}>
                  <Plus className="mr-2 h-4 w-4" />
                  {isCreatingSession ? 'Chargement...' : 'Ajouter un moyen de paiement'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Alert className="mt-6">
          <Info className="h-4 w-4" />
          <AlertTitle>Sécurité et confidentialité</AlertTitle>
          <AlertDescription>
            Vos informations de paiement sont sécurisées par Stripe. RevaCours ne stocke jamais directement vos données bancaires.
            Les prélèvements automatiques ne seront effectués que pour les factures validées et avec votre consentement.
          </AlertDescription>
        </Alert>
      </div>
    </div>
  )
}
